import { Button, Result, Skeleton } from 'antd'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useWorkspaceState } from '../WorkspaceContext'
import { canAccessTeamManagement } from './teamPermissions'

/**
 * VistoeraTASK-00525 — route guard for `/workspace/:id/team/*`.
 *
 * Display-only: it uses the role the server returned for THIS load of the
 * Workspace (`GET /saas/workspaces/:id` → `my_role`, `team_mode_enabled`). The
 * real gate is the backend (`require_workspace_member(ADMIN)` + Team Mode on
 * every `/team/*` call), so typing the URL as a Member only ever shows this
 * 403 page — the API calls behind it would be refused anyway.
 */
export default function RequireTeamManager({ children }: { children: ReactNode }) {
  const { workspace, loading } = useWorkspaceState()
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('team')

  if (loading && !workspace) return <Skeleton active paragraph={{ rows: 8 }} />
  if (!canAccessTeamManagement(workspace)) {
    return (
      <Result
        status="403"
        title={t('guard.title')}
        subTitle={workspace?.team_mode_enabled ? t('guard.notManager') : t('guard.notEnabled')}
        extra={
          <Button type="primary" onClick={() => navigate(`/workspace/${workspaceId}/overview`, { replace: true })}>
            {t('guard.back')}
          </Button>
        }
      />
    )
  }
  return <>{children}</>
}
